// SEO tool-page definitions. Each maps one engine to one exact-match landing page.
// Copy rules: honest claims only — first conversion free (guest funnel), files are
// processed in memory and never stored, redaction is permanent, protect uses AES-256.

export const SITE_URL = "https://doclyx.com";

export const TOOLS = [
  {
    slug: "compress-pdf",
    engine: "compress",
    h1: "Compress PDF",
    title: "Compress PDF Online — Reduce PDF File Size Free | Doclyx",
    description:
      "Compress PDF files by up to 85% while keeping text selectable and searchable. First conversion free, no account needed. Files are never stored.",
    intro:
      "Shrink oversized PDFs for email attachment limits, portal upload caps, and data-room requirements — without turning pages into blurry images.",
    steps: [
      "Drop your PDF onto the tool above",
      "Doclyx rebuilds streams and recompresses images while preserving the text layer",
      "Download a dramatically smaller PDF, ready to send",
    ],
    faqs: [
      {
        q: "Will compressing a PDF reduce its quality?",
        a: "Text stays fully selectable and searchable. Images are recompressed aggressively, which is where most of the size savings come from — for typical business documents the visual difference is minimal.",
      },
      {
        q: "Is this PDF compressor free?",
        a: "Your first conversion is free with no account. After that, plans start at $5/month with 20 conversions included, and $50/month for unlimited use.",
      },
      {
        q: "Are my files stored on your servers?",
        a: "No. Documents are processed in memory and the output is streamed back to you. Nothing is retained — there is no file history to leak.",
      },
    ],
    related: ["merge-pdf", "split-pdf", "redact-pdf"],
    variantLinks: ["compress-pdf-to-100kb", "compress-pdf-to-200kb", "compress-pdf-to-500kb", "compress-pdf-to-1mb", "compress-pdf-to-2mb"],
  },
  {
    slug: "merge-pdf",
    engine: "merge",
    h1: "Merge PDF",
    title: "Merge PDF Files into One — Free PDF Combiner | Doclyx",
    description:
      "Combine multiple PDFs into a single ordered document. Drag files to set the exact sequence. First merge free, no sign-up — files never stored.",
    intro:
      "Combine diligence bundles, closing sets, and board packs into one clean PDF — in exactly the order you choose.",
    steps: [
      "Drop two or more PDFs onto the tool",
      "Drag files into the exact order you need",
      "Execute and download one merged PDF",
    ],
    faqs: [
      {
        q: "How many PDFs can I merge at once?",
        a: "Up to 20 files per run, 50MB combined. Each document lands in the merged output in the order you set.",
      },
      {
        q: "Can I control the page order of the merged file?",
        a: "Yes. Files are merged in the sequence shown in the file list — drag the handles to reorder before merging.",
      },
      {
        q: "Is merging free?",
        a: "The first run is free with no account. Regular use is covered by plans from $5/month.",
      },
    ],
    related: ["split-pdf", "compress-pdf", "jpg-to-pdf"],
  },
  {
    slug: "jpg-to-pdf",
    engine: "img2pdf",
    h1: "JPG to PDF",
    title: "JPG to PDF Converter — Turn Images into PDF Free | Doclyx",
    description:
      "Convert JPG, PNG, and WEBP images into a clean multi-page PDF at full resolution. One image per page. First conversion free, files never stored.",
    intro:
      "Turn scans, photos, and receipts into a single clean PDF — one full-resolution image per page.",
    steps: [
      "Drop JPG, PNG, or WEBP images onto the tool",
      "Drag to set the page order",
      "Download a multi-page PDF, one image per page",
    ],
    faqs: [
      {
        q: "Which image formats are supported?",
        a: "JPG, PNG, and WEBP. You can mix formats in a single conversion.",
      },
      {
        q: "Are images compressed during conversion?",
        a: "Images are embedded at full resolution — what you upload is what lands on the page.",
      },
      {
        q: "Is it free to convert images to PDF?",
        a: "Your first conversion is free with no account. Plans for regular use start at $5/month.",
      },
    ],
    related: ["pdf-to-png", "merge-pdf", "compress-pdf"],
  },
  {
    slug: "extract-text-from-pdf",
    engine: "extract",
    h1: "Extract Text from PDF",
    title: "Extract Text from PDF Online — Free PDF Text Extractor | Doclyx",
    description:
      "Pull the complete text layer out of any PDF as clean, ordered plain text — ready for search, review, or AI pipelines. First extraction free.",
    intro:
      "Get every word out of a PDF as clean plain text — for contract review, full-text search, or feeding AI pipelines.",
    steps: [
      "Drop your PDF onto the tool",
      "Doclyx pulls the full text layer, page by page, in reading order",
      "Copy or download the plain-text output",
    ],
    faqs: [
      {
        q: "Does this work on scanned PDFs?",
        a: "Text extraction reads the embedded text layer. Scanned documents without a text layer have no text to extract — for those, use PDF to PNG and an OCR tool.",
      },
      {
        q: "What do I get back?",
        a: "Clean, ordered plain text covering every page — no layout artifacts, no column jumbling beyond what the source file contains.",
      },
      {
        q: "Is extraction free?",
        a: "The first run is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["pdf-to-word", "edit-pdf", "split-pdf"],
  },
  {
    slug: "split-pdf",
    engine: "pages",
    h1: "Split PDF",
    title: "Split PDF Online — Extract, Reorder & Remove Pages | Doclyx",
    description:
      "Split a PDF by extracting exact page ranges like 2-3, 5, 8-12 — or visually reorder and exclude pages with drag-and-drop. First split free.",
    intro:
      "Carve exact pages out of a large PDF — extract exhibits, drop cover pages, or rebuild the document in a new order.",
    steps: [
      "Drop your PDF onto the tool",
      "Pick pages visually — drag to reorder, tap to exclude — or type a range like 2-3, 5, 8-12",
      "Download a new PDF with just the pages you kept",
    ],
    faqs: [
      {
        q: "Can I extract specific pages from a PDF?",
        a: "Yes — type exact ranges (for example 2-3, 5, 8-12) or select pages visually on live page previews.",
      },
      {
        q: "Can I reorder pages instead of splitting?",
        a: "Yes. Drag page thumbnails into any order and the output PDF follows that sequence.",
      },
      {
        q: "Is there a page limit?",
        a: "No page cap on splitting — large diligence documents are fine.",
      },
    ],
    related: ["merge-pdf", "rotate-pdf", "compress-pdf"],
  },
  {
    slug: "rotate-pdf",
    engine: "rotate",
    h1: "Rotate PDF",
    title: "Rotate PDF Online — 90, 180 or 270 Degrees Free | Doclyx",
    description:
      "Rotate all pages of a PDF by 90, 180, or 270 degrees in one pass. Fix sideways or upside-down scans. First conversion free, files never stored.",
    intro:
      "Fix sideways scans and mixed-orientation bundles — rotate every page 90, 180, or 270 degrees in one pass.",
    steps: [
      "Drop your PDF onto the tool",
      "Choose 90, 180, or 270 degrees",
      "Download the corrected document",
    ],
    faqs: [
      {
        q: "Can I rotate just one page?",
        a: "The Rotate engine turns the whole document. To rotate individual pages, use the Edit PDF tool, which rotates pages independently.",
      },
      {
        q: "Does rotation affect quality?",
        a: "No — rotation changes page orientation metadata only. Text and images are untouched.",
      },
      {
        q: "Is rotating a PDF free?",
        a: "Your first conversion is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["split-pdf", "edit-pdf", "compress-pdf"],
  },
  {
    slug: "pdf-to-png",
    engine: "pdf2img",
    h1: "PDF to PNG",
    title: "PDF to PNG Converter — Crisp 150 DPI Images | Doclyx",
    description:
      "Render every PDF page as a crisp 150 DPI PNG, delivered as a single zip. Perfect for decks, previews, and review markup. First conversion free.",
    intro:
      "Turn PDF pages into crisp 150 DPI PNG images — for slide inserts, page previews, and annotation workflows.",
    steps: [
      "Drop your PDF onto the tool",
      "Each page renders at 150 DPI",
      "Download a zip containing one PNG per page",
    ],
    faqs: [
      {
        q: "What resolution are the images?",
        a: "150 DPI — sharp enough for decks, thumbnails, and on-screen review markup.",
      },
      {
        q: "How many pages can I convert?",
        a: "Up to 20 pages per run, delivered as a single zip of PNG files.",
      },
      {
        q: "Is PDF to PNG free?",
        a: "The first conversion is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["pdf-to-jpg", "jpg-to-pdf", "compress-pdf"],
  },
  {
    slug: "pdf-to-word",
    engine: "pdf2docx",
    h1: "PDF to Word",
    title: "PDF to Word Converter — Editable DOCX Online | Doclyx",
    description:
      "Convert text-based PDFs into editable Word documents with titles, headings, and clean paragraphs. First conversion free, files never stored.",
    intro:
      "Turn a finished PDF back into an editable Word document — for redlining, clause edits, and repurposing executed documents.",
    steps: [
      "Drop a text-based PDF onto the tool",
      "Doclyx rebuilds it as a DOCX with a title, page headings, and clean paragraphs",
      "Download and edit in Word, Google Docs, or Pages",
    ],
    faqs: [
      {
        q: "Will the Word file keep my exact layout?",
        a: "The conversion rebuilds clean, editable paragraphs and headings rather than preserving pixel-perfect layout — ideal for editing text, not for reproducing design.",
      },
      {
        q: "Does it work on scanned PDFs?",
        a: "No — the converter reads the text layer, so the PDF needs real text. Scans without a text layer won't convert.",
      },
      {
        q: "Is PDF to Word free?",
        a: "Your first conversion is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["extract-text-from-pdf", "edit-pdf", "merge-pdf"],
  },
  {
    slug: "watermark-pdf",
    engine: "watermark",
    h1: "Add Watermark to PDF",
    title: "Watermark PDF Online — Stamp CONFIDENTIAL & More | Doclyx",
    description:
      "Add a diagonal text watermark to every page of a PDF — CONFIDENTIAL, DRAFT, or any custom text. First conversion free, files never stored.",
    intro:
      "Stamp every page with a subtle diagonal watermark — visible enough to deter leaks, light enough to stay readable.",
    steps: [
      "Drop your PDF onto the tool",
      "Type your watermark text — CONFIDENTIAL, DRAFT, a client name",
      "Download the stamped document",
    ],
    faqs: [
      {
        q: "Can I customize the watermark text?",
        a: "Yes — any short text works. Common choices are CONFIDENTIAL, DRAFT, or the recipient's name for leak deterrence.",
      },
      {
        q: "Does the watermark cover the content?",
        a: "The stamp is placed diagonally at low visual weight — readable content stays readable.",
      },
      {
        q: "Is watermarking free?",
        a: "The first run is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["redact-pdf", "password-protect-pdf", "edit-pdf"],
  },
  {
    slug: "redact-pdf",
    engine: "redact",
    h1: "Redact PDF",
    title: "Redact PDF Permanently — True Redaction Online | Doclyx",
    description:
      "Permanently redact PDF text and regions — blackout boxes or exact-phrase search, burned into the file forever. Not a cosmetic overlay. First run free.",
    intro:
      "True redaction for due diligence and external counsel copies: content is removed from the file itself, not hidden under a black rectangle anyone can peel off.",
    steps: [
      "Drop your PDF onto the tool and review live page previews",
      "Draw blackout boxes, or search exact phrases to redact — optionally limited to page ranges",
      "Download a PDF where the redacted content no longer exists",
    ],
    faqs: [
      {
        q: "Is the redaction actually permanent?",
        a: "Yes. Doclyx applies true redaction — the underlying text and image data is removed from the file, not covered by an annotation. Copy-paste and text extraction return nothing from redacted regions.",
      },
      {
        q: "Can I redact a specific phrase everywhere it appears?",
        a: "Yes — enter exact text and every match is redacted, optionally scoped to a page range like 2-3, 5, 8-12.",
      },
      {
        q: "Is this suitable for legal or M&A documents?",
        a: "That is exactly what it is built for — diligence redaction, privilege removal, and sanitizing documents before external sharing. Combined with zero file retention, sensitive content never persists on our side either.",
      },
    ],
    related: ["remove-pdf-metadata", "password-protect-pdf", "watermark-pdf"],
  },
  {
    slug: "password-protect-pdf",
    engine: "protect",
    h1: "Password Protect PDF",
    title: "Password Protect PDF — AES-256 Encryption Online | Doclyx",
    description:
      "Encrypt a PDF with bank-grade AES-256 so it only opens with your password. First conversion free, no account — files are never stored.",
    intro:
      "Lock a PDF with AES-256 encryption so it only opens with your password — before it goes into an email or a shared drive.",
    steps: [
      "Drop your PDF onto the tool",
      "Set a password (4–64 characters)",
      "Download the encrypted PDF — it now requires the password to open",
    ],
    faqs: [
      {
        q: "How strong is the encryption?",
        a: "AES-256 — the same standard used by banks and governments. Without the password, the document does not open.",
      },
      {
        q: "Do you keep a copy of my password?",
        a: "No. The password is used for the encryption pass and discarded. Files are never stored either — lose the password and the document stays locked.",
      },
      {
        q: "Is password protection free?",
        a: "Your first conversion is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["unlock-pdf", "redact-pdf", "watermark-pdf"],
  },
  {
    slug: "unlock-pdf",
    engine: "unlock",
    h1: "Unlock PDF",
    title: "Unlock PDF — Remove a Known Password Online | Doclyx",
    description:
      "Remove password protection from your own PDF when you know the current password, leaving a clean, open document. First conversion free.",
    intro:
      "Remove password protection from your own PDF when you know the password — leaving a clean document that opens for everyone.",
    steps: [
      "Drop the protected PDF onto the tool",
      "Enter the current password",
      "Download an unlocked copy that opens without a password",
    ],
    faqs: [
      {
        q: "Can this crack a PDF password I don't know?",
        a: "No — and it shouldn't. Unlock removes protection only when you supply the current password. It is for your own documents, not for bypassing someone else's security.",
      },
      {
        q: "Will the unlocked file keep its content intact?",
        a: "Yes — decryption removes only the password requirement. Every page, image, and text layer is preserved.",
      },
      {
        q: "Is unlocking free?",
        a: "The first conversion is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["password-protect-pdf", "compress-pdf", "edit-pdf"],
  },
  {
    slug: "remove-pdf-metadata",
    engine: "scrub",
    h1: "Remove PDF Metadata",
    title: "Remove Metadata from PDF — Scrub Hidden Data Free | Doclyx",
    description:
      "Strip hidden PDF metadata — author names, edit history, timestamps, embedded XML — that leaks deal context. First scrub free, files never stored.",
    intro:
      "Every PDF carries hidden metadata — author names, edit history, timestamps, embedded XML — that can leak deal context. Scrub it clean before the document leaves your hands.",
    steps: [
      "Drop your PDF onto the tool",
      "Doclyx strips the document info dictionary and embedded XML metadata",
      "Download an identical-looking PDF with the hidden data gone",
    ],
    faqs: [
      {
        q: "What metadata does a PDF actually contain?",
        a: "Typically author and creator names, the software used, creation and modification timestamps, and sometimes embedded XML with editing history — all invisible when reading the document.",
      },
      {
        q: "Does scrubbing change the visible document?",
        a: "No — pages, text, and images are untouched. Only the hidden metadata layer is removed.",
      },
      {
        q: "Why scrub metadata before sharing?",
        a: "In diligence and legal workflows, metadata can reveal who drafted a document, when, and in what tool — context you may not want the other side to have.",
      },
    ],
    related: ["redact-pdf", "password-protect-pdf", "compress-pdf"],
  },
  {
    slug: "edit-pdf",
    engine: "edit",
    h1: "Edit PDF",
    title: "Edit PDF Online — Add Text, Rotate & Delete Pages | Doclyx",
    description:
      "Edit a PDF directly: place new text anywhere on a page, rotate individual pages, and delete pages you don't need. First edit free, no account.",
    intro:
      "Make fast, surgical edits to a finished PDF — drop new text anywhere, rotate a single page, or remove pages you don't need.",
    steps: [
      "Drop your PDF onto the tool and review live page previews",
      "Click to place text (small, medium, or large), rotate individual pages, or mark pages for deletion",
      "Download the edited PDF",
    ],
    faqs: [
      {
        q: "Can I edit existing text in a PDF?",
        a: "PDF text is not reflowable like a Word document — this tool adds new text on top and manages pages. To rewrite existing content, convert to Word first, edit, and convert back.",
      },
      {
        q: "Can I rotate or delete just one page?",
        a: "Yes — rotations and deletions apply per page on the live thumbnails.",
      },
      {
        q: "Is editing free?",
        a: "Your first run is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["watermark-pdf", "split-pdf", "pdf-to-word"],
  },
  {
    slug: "pdf-invoice-generator",
    engine: "invoice",
    h1: "Invoice Generator",
    title: "Invoice Generator — Create a PDF Invoice Free | Doclyx",
    description:
      "Build a polished invoice PDF with line items, quantities, tax, and notes — download it or email it to your client with a secure payment-ready link.",
    intro:
      "Create a client-ready invoice PDF in seconds — line items, quantities, automatic tax math, and notes — then download it or email it straight to your client.",
    steps: [
      "Enter your client details and line items — quantity, price, tax",
      "Doclyx builds a polished invoice PDF with totals calculated exactly",
      "Download the PDF, or email it to your client with a secure 30-day download link",
    ],
    faqs: [
      {
        q: "Do I need to upload a file?",
        a: "No — the invoice is built from the details you enter: client, line items, quantities, prices, tax, and notes.",
      },
      {
        q: "Can I send the invoice to my client directly?",
        a: "Yes — the tool can email the invoice with a secure, time-limited download link, and you can copy the link to share however you like.",
      },
      {
        q: "Is the invoice generator free?",
        a: "Your first invoice is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["extract-text-from-pdf", "merge-pdf", "compress-pdf"],
  },
  {
    slug: "compress-pdf-to-100kb",
    engine: "compress",
    initialParams: { targetKb: 100 },
    h1: "Compress PDF to 100KB",
    title: "Compress PDF to 100KB Online — Meet Strict Upload Caps | Doclyx",
    description:
      "Shrink a PDF under a 100KB limit for government portals, visa applications, and strict upload forms. Images are reduced step by step until your file fits.",
    intro:
      "Government portals, visa applications, and strict upload forms often cap files at 100KB. This tool reduces your PDF step by step until it fits under the cap — or gets as close as the content allows.",
    steps: [
      "Drop your PDF onto the tool — the 100KB target is preselected",
      "Doclyx recompresses images in stages until the file fits under 100KB",
      "Download the smaller PDF and check it against your portal's limit",
    ],
    faqs: [
      {
        q: "Can every PDF be compressed to 100KB?",
        a: "Image-heavy files compress furthest. A long text document with many photos can almost always reach 100KB; a text-only PDF is usually small already. If the target is physically unreachable, you get the smallest version the content allows.",
      },
      {
        q: "Will my text stay readable at 100KB?",
        a: "Yes — the text layer is never rasterized. Compression comes from downsampling and recompressing embedded images.",
      },
      {
        q: "Is the 100KB compressor free?",
        a: "Your first conversion is free with no account. Plans for regular use start at $5/month.",
      },
    ],
    related: ["compress-pdf-to-500kb", "compress-pdf-to-1mb", "compress-pdf"],
  },
  {
    slug: "compress-pdf-to-200kb",
    engine: "compress",
    initialParams: { targetKb: 200 },
    h1: "Compress PDF to 200KB",
    title: "Compress PDF to 200KB Online — Hit a 200KB Upload Limit | Doclyx",
    description:
      "Get a PDF under a 200KB cap for application portals and strict email servers. Step-by-step image reduction until your file fits. First conversion free.",
    intro:
      "Application portals and strict corporate mail servers often draw the line at 200KB. This tool shrinks your PDF in stages until it clears the bar — keeping text crisp throughout.",
    steps: [
      "Drop your PDF onto the tool — the 200KB target is preselected",
      "Images are downsampled in stages until the file fits",
      "Download and upload with confidence",
    ],
    faqs: [
      {
        q: "What if my PDF can't reach 200KB?",
        a: "You get the smallest achievable version — text-only PDFs are typically tiny already, while very image-dense files have a floor. Removing unneeded pages first (use Split PDF) lowers that floor further.",
      },
      {
        q: "Does compressing to 200KB make text blurry?",
        a: "No. Text remains vector-sharp and selectable — only embedded images are recompressed.",
      },
      {
        q: "Is it free?",
        a: "The first conversion is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["compress-pdf-to-100kb", "compress-pdf-to-500kb", "split-pdf"],
  },
  {
    slug: "compress-pdf-to-500kb",
    engine: "compress",
    initialParams: { targetKb: 500 },
    h1: "Compress PDF to 500KB",
    title: "Compress PDF to 500KB Online — Meet a 500KB Upload Cap | Doclyx",
    description:
      "Reduce a PDF below 500KB for claim portals, application systems, and email gateways. Staged image compression preserves selectable text. First run free.",
    intro:
      "Insurance claim portals, application systems, and email gateways commonly cap uploads at 500KB. This tool compresses your PDF in stages until it fits — without touching the text layer.",
    steps: [
      "Drop your PDF onto the tool — the 500KB target is preselected",
      "Doclyx reduces images step by step until the file is under 500KB",
      "Download the result, ready for your portal",
    ],
    faqs: [
      {
        q: "How close to 500KB will it get?",
        a: "If the target is reachable, the output lands under 500KB exactly. If not, you receive the smallest version your document's content allows.",
      },
      {
        q: "Is my document stored anywhere?",
        a: "Never. Files are processed in memory and the result is streamed straight back — there is no retention.",
      },
      {
        q: "Is compressing to 500KB free?",
        a: "Your first conversion is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["compress-pdf-to-1mb", "compress-pdf-to-200kb", "compress-pdf"],
  },
  {
    slug: "compress-pdf-to-1mb",
    engine: "compress",
    initialParams: { targetKb: 1000 },
    h1: "Compress PDF to 1MB",
    title: "Compress PDF to 1MB Online — Fit the 1MB Limit Free | Doclyx",
    description:
      "Compress a PDF under 1MB for email attachments and upload portals. Images are reduced step by step until your file fits — text stays sharp. First run free.",
    intro:
      "1MB is the classic email attachment and portal cap. This tool takes your PDF under the limit with staged image compression — text stays perfectly sharp and selectable.",
    steps: [
      "Drop your PDF onto the tool — the 1MB target is preselected",
      "Images are recompressed in stages until the file fits under 1MB",
      "Download a document that sails through the 1MB cap",
    ],
    faqs: [
      {
        q: "Will a 1MB target hurt readability?",
        a: "For typical business documents, no — text is untouched and image quality is reduced only as far as needed to hit the target.",
      },
      {
        q: "What if my file is still over 1MB?",
        a: "Extremely photo-dense documents have a compression floor. You always get the smallest achievable result; splitting the document or compressing to 2MB are practical fallbacks.",
      },
      {
        q: "Is the 1MB compressor free?",
        a: "The first conversion is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["compress-pdf-to-2mb", "compress-pdf-to-500kb", "merge-pdf"],
  },
  {
    slug: "compress-pdf-to-2mb",
    engine: "compress",
    initialParams: { targetKb: 2000 },
    h1: "Compress PDF to 2MB",
    title: "Compress PDF to 2MB Online — Data-Room & CRM Uploads | Doclyx",
    description:
      "Shrink a PDF under 2MB for data rooms, CRMs, and shared drives. Staged image compression keeps text selectable. First conversion free, files never stored.",
    intro:
      "Data rooms, CRMs, and shared drives often cap files at 2MB. This tool compresses your PDF in stages until it fits — ideal for large diligence documents and image-heavy reports.",
    steps: [
      "Drop your PDF onto the tool — the 2MB target is preselected",
      "Images are reduced step by step until the file is under 2MB",
      "Download and upload to your data room or CRM",
    ],
    faqs: [
      {
        q: "Does it work on large scanned documents?",
        a: "Yes — scans are mostly images, which is exactly what target compression reduces. Multi-megabyte scans routinely drop under 2MB.",
      },
      {
        q: "Will my PDF still look professional?",
        a: "Text remains vector-sharp. Images are reduced only as far as the target requires — the ladder stops as soon as the file fits.",
      },
      {
        q: "Is compressing to 2MB free?",
        a: "Your first conversion is free with no account. Plans start at $5/month.",
      },
    ],
    related: ["compress-pdf-to-1mb", "compress-pdf", "prepare-data-room-documents"],
  },
  {
    slug: "pdf-to-jpg",
    engine: "pdf2img",
    initialParams: { outFormat: "jpg" },
    h1: "PDF to JPG",
    title: "PDF to JPG Converter — 150 DPI Images Online | Doclyx",
    description:
      "Convert PDF pages to compact JPG images at 150 DPI, delivered as a single zip. Smaller files than PNG — ideal for sharing and previews. First run free.",
    intro:
      "Turn PDF pages into compact JPG images at 150 DPI — smaller than PNG, perfect for sharing, previews, and uploads that demand JPEG.",
    steps: [
      "Drop your PDF onto the tool — JPG output is preselected",
      "Each page renders at 150 DPI as a JPEG",
      "Download a zip containing one JPG per page",
    ],
    faqs: [
      {
        q: "What's the difference between PDF to JPG and PDF to PNG?",
        a: "JPG files are significantly smaller — better for sharing and uploads. PNG preserves exact pixels — better for markup and further editing. Both render at 150 DPI here.",
      },
      {
        q: "How many pages can I convert?",
        a: "Up to 20 pages per run, delivered as a single zip of JPG files.",
      },
      {
        q: "Is PDF to JPG free?",
        a: "Your first conversion is free with no account. Regular use starts at $5/month.",
      },
    ],
    related: ["pdf-to-png", "jpg-to-pdf", "compress-pdf"],
  },
  {
    slug: "smallpdf-alternative",
    contentOnly: true,
    h1: "Smallpdf Alternative",
    title: "Smallpdf Alternative — Flat Pricing, Zero File Retention | Doclyx",
    description:
      "Looking for a Smallpdf alternative? Doclyx offers 15 PDF engines, a $5/month entry plan, true permanent redaction, and zero file retention — no per-day task caps.",
    intro:
      "Smallpdf is a solid everyday tool, but its free tier caps you at roughly two tasks per day and pushes heavy users toward a ~$12/month subscription. If you handle sensitive deal documents, Doclyx is built for you: flat pricing, zero file retention, and true permanent redaction.",
    table: {
      head: ["", "Smallpdf", "Doclyx"],
      rows: [
        ["Free tier", "~2 tasks per day, then wait or upgrade", "First conversion free — no account, no watermarks"],
        ["Entry plan", "~$12/month Pro", "$5/month — 20 conversions included"],
        ["Unlimited use", "Pro plan required", "$50/month flat, every engine"],
        ["File retention", "Files deleted after a delay (typically 1–2 hours)", "Never stored — processed in memory, streamed back"],
        ["Permanent redaction", "Not a core feature", "True redaction — content removed from the file"],
        ["Positioning", "General consumer PDF tasks", "High-stakes M&A, legal, and diligence documents"],
      ],
    },
    sections: [
      {
        heading: "Why people look for a Smallpdf alternative",
        paras: [
          "The free tier's daily task cap interrupts real work — merge two files and you're done for the day. And when documents are confidential, 'deleted after a delay' still means your file sat on someone else's server.",
          "Doclyx processes every document in memory and never stores it: there is no file history, no retention window, and nothing to leak. Redaction is permanent — blacked-out content is removed from the file itself, not hidden under an annotation.",
        ],
      },
      {
        heading: "What you get with Doclyx",
        paras: [
          "Fifteen engines cover the full document lifecycle: compress (including exact target sizes like 100KB or 1MB), merge, split, rotate, PDF to Word, JPG/PNG conversion, watermark, permanent redaction, AES-256 password protection, metadata scrubbing, page editing, and a built-in invoice generator.",
          "Pricing is flat and predictable: $5/month for 20 conversions, $50/month unlimited, every plan starting with a 14-day free trial.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is Doclyx really cheaper than Smallpdf?",
        a: "The entry plan is $5/month versus Smallpdf's ~$12/month Pro. Doclyx's $50/month unlimited tier is aimed at professionals who process documents daily.",
      },
      {
        q: "Does Doclyx watermark my files?",
        a: "Never. There are no watermarks on any output, free run included.",
      },
      {
        q: "Can I try Doclyx without an account?",
        a: "Yes — the first conversion is completely free with no sign-up, straight from any tool page.",
      },
    ],
    cta: { label: "Try Doclyx free — compress a PDF now", href: "/compress-pdf" },
    related: ["ilovepdf-alternative", "redact-pdf", "secure-pdf-tools"],
  },
  {
    slug: "ilovepdf-alternative",
    contentOnly: true,
    h1: "iLovePDF Alternative",
    title: "iLovePDF Alternative — No Daily Caps, Zero Retention | Doclyx",
    description:
      "Need an iLovePDF alternative without daily task caps? Doclyx gives you 16 engines, permanent redaction, AES-256 protection, and zero file retention — from $5/month.",
    intro:
      "iLovePDF covers a lot of ground, but free users hit daily task caps, a ~100MB upload ceiling, and ad-supported pages. Doclyx is the quieter, professional alternative: no daily caps on paid plans, files that are never stored, and redaction that is actually permanent.",
    table: {
      head: ["", "iLovePDF", "Doclyx"],
      rows: [
        ["Free tier", "Daily task caps + ads", "First conversion free — no account, no ads"],
        ["Entry plan", "Premium subscription", "$5/month — 20 conversions included"],
        ["Unlimited use", "Premium required", "$50/month flat, every engine"],
        ["File retention", "Files held for a processing window, then deleted", "Never stored — processed in memory"],
        ["Permanent redaction", "Not available", "True redaction — content physically removed"],
        ["Document focus", "Everyday PDF tasks", "M&A, legal, and diligence workflows"],
      ],
    },
    sections: [
      {
        heading: "Why people look for an iLovePDF alternative",
        paras: [
          "Task caps and upload ceilings are friction designed for upgrades — fair enough for casual use, but a blocker when you are processing a diligence bundle on a deadline. And for confidential documents, any retention window is a window too long.",
          "Doclyx never stores your files. Documents are processed in memory and the result is streamed back; the moment your download finishes, nothing remains.",
        ],
      },
      {
        heading: "Built for high-stakes documents",
        paras: [
          "Beyond the everyday engines — compress with exact target sizes, merge, split, rotate, PDF to Word, JPG/PNG conversion — Doclyx includes the tools deal teams actually need: permanent redaction by drawn box or exact phrase, metadata scrubbing, AES-256 password protection, page-level editing, and a client-ready invoice generator.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is there a free way to try Doclyx?",
        a: "Yes — the first conversion is free with no account, directly from any tool page. Plans then start at $5/month with a 14-day free trial.",
      },
      {
        q: "Can Doclyx handle large files?",
        a: "Up to 50MB per run, 15MB per file on the entry plan — sized for business documents, contracts, and reports.",
      },
      {
        q: "Does Doclyx show ads?",
        a: "No. The product is paid for by subscriptions, not advertising.",
      },
    ],
    cta: { label: "Try Doclyx free — merge your PDFs now", href: "/merge-pdf" },
    related: ["smallpdf-alternative", "adobe-acrobat-alternative", "sign-pdf"],
  },
  {
    slug: "adobe-acrobat-alternative",
    contentOnly: true,
    h1: "Adobe Acrobat Alternative",
    title: "Adobe Acrobat Alternative Online — Lighter & Cheaper | Doclyx",
    description:
      "A lighter Adobe Acrobat alternative for document-heavy teams: 15 focused PDF engines in the browser, $5/month entry, zero file retention, permanent redaction.",
    intro:
      "Acrobat is the incumbent — and priced like one, at roughly $13–20/month per user for the full suite. If what you actually need is fast, secure document processing in the browser, Doclyx delivers the everyday 95% for a fraction of the cost, with privacy Acrobat's cloud can't match.",
    table: {
      head: ["", "Adobe Acrobat", "Doclyx"],
      rows: [
        ["Price", "~$12.99–19.99/month per user", "$5/month entry, $50/month unlimited"],
        ["Form factor", "Desktop suite + cloud tools", "Fast browser workspace — nothing to install"],
        ["File retention", "Documents processed in Adobe's cloud", "Never stored — processed in memory"],
        ["Redaction", "Pro tier feature", "Permanent redaction included in every plan"],
        ["Learning curve", "Full desktop application", "Open a tool, drop a file, done"],
        ["Best for", "Enterprises standardized on Adobe", "Deal teams, counsel, and operators"],
      ],
    },
    sections: [
      {
        heading: "Why teams look beyond Acrobat",
        paras: [
          "Acrobat is a desktop suite with a cloud attached — powerful, but heavy: per-seat pricing, installations, updates, and documents flowing through Adobe's cloud. For many teams the real need is simpler: compress to an exact size, merge a closing set, redact a figure, protect a file — quickly and privately.",
          "Doclyx is exactly that: fifteen focused engines in the browser, permanent redaction in every plan, AES-256 protection, metadata scrubbing, and zero file retention by architecture.",
        ],
      },
      {
        heading: "Honest trade-offs",
        paras: [
          "Acrobat still wins on deep form authoring, OCR breadth, and enterprise integrations. If you live in PDF forms all day, stay put. If you process confidential deal and legal documents and want speed, flat pricing, and zero retention — Doclyx is the sharper tool.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much cheaper is Doclyx than Acrobat?",
        a: "Acrobat runs roughly $13–20 per user per month. Doclyx starts at $5/month with 20 conversions, and $50/month covers unlimited use across all 16 engines.",
      },
      {
        q: "Do I need to install anything?",
        a: "No — everything runs in the browser. Your first conversion is free with no account.",
      },
      {
        q: "Is redaction included?",
        a: "Yes — true permanent redaction is in every plan, including the free first run. Acrobat gates redaction behind its Pro tier.",
      },
    ],
    cta: { label: "Try Doclyx free — redact a PDF now", href: "/redact-pdf" },
    related: ["smallpdf-alternative", "ilovepdf-alternative", "secure-pdf-tools"],
  },
  {
    slug: "redact-pdf-for-due-diligence",
    contentOnly: true,
    h1: "How to Redact a PDF for Due Diligence",
    title: "How to Redact a PDF for Due Diligence — A Defensible Workflow | Doclyx",
    description:
      "Redacting deal documents for due diligence: what to remove, why cosmetic blackout boxes fail, and a workflow that holds up when the other side's counsel checks.",
    intro:
      "Due diligence means sharing sensitive documents with parties you don't yet trust. Redaction is where deals quietly go wrong — most 'redacted' PDFs circulating in data rooms still contain the text under the black boxes.",
    sections: [
      {
        heading: "What typically needs redacting",
        paras: [
          "Customer names and pricing in contracts, employee personal data in HR documents, bank details in financial exhibits, privileged counsel notes, and commercial terms that would strengthen the other side's negotiating position.",
          "The test is simple: if the document leaked in full tomorrow, which sentences would you regret? Those are your redaction targets.",
        ],
      },
      {
        heading: "Why most redaction fails",
        paras: [
          "Drawing a black rectangle over text in a standard editor creates an annotation — a layer on top of the page. The underlying text remains in the file: anyone can select it, copy it, or remove the rectangle. Several publicized data-room incidents trace back to exactly this mistake.",
          "True redaction removes the content from the file itself. Doclyx applies redactions permanently: after processing, text extraction and copy-paste return nothing from the redacted regions — there is nothing left to peel off.",
        ],
      },
      {
        heading: "A defensible workflow",
        paras: [
          "One: redact by exact phrase where you can — names, account numbers, specific figures — so nothing is missed by eye. Two: draw boxes over regions like signatures or logo blocks that search can't catch. Three: verify the output — open the redacted PDF and try to select or search the removed text. Four: scrub metadata before sharing, so author names and edit history don't travel with the file.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is Doclyx's redaction permanent?",
        a: "Yes. Redacted text and image regions are physically removed from the file — not covered by an annotation. Copy-paste, text extraction, and layer inspection all return nothing.",
      },
      {
        q: "Can I redact a phrase everywhere it appears in a long document?",
        a: "Yes — enter the exact text and every match is redacted across the document, optionally limited to a page range like 2-3, 5, 8-12.",
      },
      {
        q: "Should I also remove metadata before sharing?",
        a: "Yes. PDF metadata routinely carries author names, timestamps, and editing history — context the other side doesn't need. Run Remove PDF Metadata after redacting.",
      },
    ],
    cta: { label: "Redact a PDF now — first run free", href: "/redact-pdf" },
    related: ["redact-pdf", "remove-pdf-metadata", "prepare-data-room-documents"],
  },
  {
    slug: "prepare-data-room-documents",
    contentOnly: true,
    h1: "How to Prepare Documents for a Data Room",
    title: "How to Prepare Documents for a Data Room — Checklist | Doclyx",
    description:
      "A practical checklist for data-room-ready PDFs: compress under portal caps, merge ordered bundles, redact sensitive figures, and scrub metadata before upload.",
    intro:
      "Data rooms have rules: file size caps, naming conventions, and a buyer who will read everything — including the parts you forgot were in the file. This checklist gets a document set upload-ready.",
    sections: [
      {
        heading: "1. Compress under the portal cap",
        paras: [
          "Most data-room portals cap uploads at 1–2MB per file. Compress every document to the cap before upload — not after the portal rejects it. Doclyx's target-size compression takes a PDF under exactly 100KB, 500KB, 1MB, or 2MB.",
        ],
      },
      {
        heading: "2. Merge bundles in a deliberate order",
        paras: [
          "A closing set or contract bundle should read in the order you choose — not in the order files happened to be named. Merge related documents into single ordered PDFs so reviewers page through them linearly.",
        ],
      },
      {
        heading: "3. Redact what the other side shouldn't see",
        paras: [
          "Pricing from other customers, employee personal data, privileged notes — remove them permanently before upload. Cosmetic black boxes are not redaction; the text underneath is still in the file.",
        ],
      },
      {
        heading: "4. Scrub metadata last",
        paras: [
          "Metadata is the final leak: author names, edit timestamps, and the software trail travel inside every PDF. Scrub it after all other edits, immediately before upload.",
        ],
      },
    ],
    faqs: [
      {
        q: "In what order should I process documents?",
        a: "Edit and redact first, merge second, compress third, scrub metadata last — metadata updates with every save, so it must be the final step.",
      },
      {
        q: "Are my documents stored anywhere during this?",
        a: "No. Doclyx processes files in memory and never stores them — which is precisely why deal teams use it for pre-data-room preparation.",
      },
      {
        q: "What about password protection?",
        a: "Where a portal or recipient requires it, protect the final PDF with AES-256 encryption and share the password over a separate channel.",
      },
    ],
    cta: { label: "Start with target-size compression — free", href: "/compress-pdf-to-1mb" },
    related: ["compress-pdf-to-1mb", "merge-pdf", "redact-pdf-for-due-diligence"],
  },
  {
    slug: "secure-pdf-tools",
    contentOnly: true,
    h1: "Secure PDF Tools That Never Store Your Files",
    title: "Secure PDF Tools — Zero File Retention, Honest Privacy | Doclyx",
    description:
      "What 'secure PDF tool' should actually mean: zero file retention, in-memory processing, AES-256 encryption, and permanent redaction — explained without hand-waving.",
    intro:
      "Every online PDF tool claims to be secure. Almost none explain what that means. Here is exactly what happens to your file on Doclyx — and the honest difference between 'never stored' and 'never uploaded'.",
    sections: [
      {
        heading: "What happens to your file on Doclyx",
        paras: [
          "Your document is uploaded over TLS, processed entirely in server memory, and the result is streamed back to your browser. No copy is written to disk, no database record holds your content, and there is no file history to browse or breach. When your download finishes, nothing remains.",
          "This is 'zero retention' — and it is architecturally different from tools that store your file and promise to delete it hours later.",
        ],
      },
      {
        heading: "An honest note about 'no upload' tools",
        paras: [
          "A newer class of tools processes PDFs entirely in your browser, so files never leave your device. That is the strongest privacy model available — and it comes with real trade-offs: weak devices struggle with large files, and some operations are limited or slow.",
          "Doclyx runs server-side because that is what aggressive multi-pass compression, permanent redaction, and 50MB documents demand. The honest promise is not 'no upload' — it is 'no storage, ever'.",
        ],
      },
      {
        heading: "The security toolkit",
        paras: [
          "Permanent redaction that physically removes content rather than hiding it. AES-256 password protection with passwords that are never logged or stored. Metadata scrubbing that strips author names, timestamps, and edit history. And a zero-retention pipeline underneath all of it.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does Doclyx keep a copy of my document?",
        a: "No. Files are processed in memory and the output is streamed back. There is no retention window because there is no storage.",
      },
      {
        q: "Is my password safe when I protect a PDF?",
        a: "The password is used for the encryption pass and immediately discarded — it is never logged, stored, or recoverable by us.",
      },
      {
        q: "Can I verify the zero-retention claim?",
        a: "Watch your browser's network tab: one upload, one download, nothing else. There is no account-side file list because there are no stored files to list.",
      },
    ],
    cta: { label: "Try a zero-retention tool — free", href: "/compress-pdf" },
    related: ["redact-pdf", "password-protect-pdf", "remove-pdf-metadata"],
  },
  {
    slug: "docusign-alternative",
    contentOnly: true,
    h1: "DocuSign Alternative",
    title: "DocuSign Alternative — E-Signatures + a Full PDF Workspace | Doclyx",
    description:
      "A DocuSign alternative with built-in e-signatures, audit trails, and client signing links — plus 15 PDF engines DocuSign doesn't have, from $5/month with no envelope math.",
    intro:
      "DocuSign pioneered e-signatures — and prices them by the envelope: 3 free per month, 5 on the $10–15 Personal plan, ~100/year on the $25–45 Standard plan. Doclyx includes legally-styled e-signatures inside a complete document workspace, so signing is where a deal closes — not where a new bill starts.",
    table: {
      head: ["", "DocuSign", "Doclyx"],
      rows: [
        ["Free tier", "3 envelopes per month", "First document free — no account, no card"],
        ["Entry plan", "Personal ~$10–15/month, 5 envelopes/month", "$5/month — 20 conversions, signing included"],
        ["Unlimited use", "Enterprise contracts, custom pricing", "$50/month flat — every engine, every signature"],
        ["Envelope anxiety", "Every send counts against your quota", "No envelope counting — send when the deal needs it"],
        ["PDF tools", "Signature-centric; editing needs other apps", "16 engines: compress to exact sizes, merge, redact permanently, protect, convert, edit, sign"],
        ["File retention", "Documents stored in your DocuSign account", "Zero retention — processed in memory, never stored"],
      ],
    },
    sections: [
      {
        heading: "Why teams look for a DocuSign alternative",
        paras: [
          "Envelope quotas turn a workflow into meter maids: teams ration sends, drafts burn quota when voided, and the features that matter — templates, bulk send, payments — sit behind higher tiers. For deal teams, the signature is the last step of a longer document pipeline that DocuSign simply doesn't cover.",
          "Doclyx covers the whole arc: compress the bundle to the data-room cap, merge it in order, redact what's privileged, scrub the metadata, protect it with AES-256 — then send it for signature with a secure 7-day link, no account required for your signer.",
        ],
      },
      {
        heading: "What signing looks like on Doclyx",
        paras: [
          "Contracts come from clean templates or your own uploaded PDF. Your client gets a branded email with a secure link, signs by drawing or typing on any device, and everyone gets the final PDF with a tamper-evident audit trail — timestamps, signer name, and document hash on a certificate page appended to the document itself.",
          "Invoices live in the same workspace: build one with live preview, email it, and the client pays through a hosted Stripe page. Signed contracts and paid invoices surface in a secure client portal your customers can revisit anytime.",
        ],
      },
      {
        heading: "Honest trade-offs",
        paras: [
          "DocuSign still leads on enterprise identity verification (KBA, biometrics), complex multi-party routing, and notarization. If you run a regulated closing room with thousands of envelopes a month, evaluate both. If you want signatures plus the entire document workflow around them — at flat pricing with zero file retention — Doclyx is the sharper tool.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a Doclyx signature legally binding?",
        a: "Doclyx signatures are electronic signatures with a full audit trail — signer identity, timestamps, and a document hash — meeting the standard requirements of ESIGN and eIDAS-style frameworks. For regulated or notarized transactions, confirm your jurisdiction's specific requirements.",
      },
      {
        q: "Does the signer need a Doclyx account?",
        a: "No. Signers open a secure, time-limited link and sign in the browser — draw or type. No downloads, no accounts.",
      },
      {
        q: "How does pricing compare for a small team?",
        a: "DocuSign Standard runs $25–45 per user per month with envelope allowances. Doclyx is $5/month entry and $50/month unlimited — not per user, not per envelope.",
      },
    ],
    cta: { label: "Sign a PDF now — free", href: "/sign-pdf" },
    related: ["smallpdf-alternative", "adobe-acrobat-alternative", "sign-pdf"],
  },
  {
    slug: "sign-pdf",
    engine: "sign",
    h1: "Sign PDF",
    title: "Sign PDF Online — Draw or Type Your Signature Free | Doclyx",
    description:
      "Sign any PDF yourself in seconds: draw or type your signature once, place it anywhere on the page, and download a permanently signed, dated PDF. First signature free.",
    intro:
      "Countersign an agreement, approve a deliverable, or sign off an invoice — without printing, scanning, or per-envelope pricing.",
    steps: [
      "Drop your PDF onto the tool and choose SIGN",
      "Capture your signature — draw it with your pointer or type your name in a handwriting style",
      "Draw a box wherever the signature belongs and download your signed, dated PDF",
    ],
    faqs: [
      {
        q: "Is a signature made with Doclyx legally binding?",
        a: "Electronic signatures are broadly legally valid (ESIGN Act in the US, eIDAS in the EU). Each signature is burned into the PDF with the signer's name and the signing date as a permanent caption.",
      },
      {
        q: "Do I need an account to sign a PDF?",
        a: "Your first signature is free with no account. After that, plans start at $5/month — unlimited signing is included in the $50/month Professional plan with a 14-day free trial.",
      },
      {
        q: "Can I send a contract to someone else to sign?",
        a: "Yes — that lives in the Legal workspace: your counterparty gets a secure signing link (no account needed on their side), and you can countersign afterwards. The Sign engine here is for signing your own documents.",
      },
    ],
    related: ["redact-pdf", "edit-pdf", "compress-pdf"],
  },
];

export const TOOL_BY_SLUG = Object.fromEntries(TOOLS.map((t) => [t.slug, t]));

// Engine → canonical tool page (primary pages only: engine pages without
// preset params — the target-size and format variants are landing pages, not
// the canonical destination for an engine).
export const ENGINE_TO_SLUG = Object.fromEntries(
  TOOLS.filter((t) => t.engine && !t.initialParams && !t.contentOnly).map((t) => [t.engine, t.slug])
);
